// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { assemblyListingSchema } from '@/assembly/api/types';
import { sortDirectionSchema } from '@/constants/sort';
import { searchTermSchema } from '@/utilities/search-term';
import queryString from 'query-string';
import { z } from 'zod';

const assemblySortPropertySchema = z.enum(['buildDate', 'product', 'location']);

export type AssemblySortProperty = z.infer<typeof assemblySortPropertySchema>;

const assemblyRegistrationStatusSchema = z.enum(['registered', 'unregistered']);

export type AssemblyRegistrationStatus = z.infer<
  typeof assemblyRegistrationStatusSchema
>;

export const assemblyFilterSchema = z.object({
  query: searchTermSchema,
  page: z.number().optional(),
  limit: z.number().optional(),
  registrationStatus: assemblyRegistrationStatusSchema.optional(),
  productIds: z.array(z.string()).optional(),
  locationIds: z.array(z.string()).optional(),
  buildDateStart: z.string().optional(),
  buildDateEnd: z.string().optional(),
  sort: assemblySortPropertySchema.optional(),
  direction: sortDirectionSchema.optional(),
});

export type AssemblyFilter = z.infer<typeof assemblyFilterSchema>;

const assemblyListingResponseSchema = z.object({
  data: assemblyListingSchema,
});

export async function getAssemblies(filter?: AssemblyFilter) {
  const params: Record<string, string | number | string[] | undefined> = {
    searchQuery: filter?.query,
    page: filter?.page ?? 1,
    limit: filter?.limit ?? 50,
  };

  // Add registration status filter
  if (filter?.registrationStatus) {
    params.registrationStatus = filter.registrationStatus;
  }

  // Add product filter
  if (filter?.productIds && filter.productIds.length > 0) {
    params.productIds = filter.productIds;
  }

  // Add location filter
  if (filter?.locationIds && filter.locationIds.length > 0) {
    params.locationIds = filter.locationIds;
  }

  // Add build date range filter
  if (filter?.buildDateStart) {
    params.buildDateStart = filter.buildDateStart;
  }
  if (filter?.buildDateEnd) {
    params.buildDateEnd = filter.buildDateEnd;
  }

  // Add sorting
  if (filter?.sort) {
    params.sortBy = filter.sort;
    // An absent direction has historically meant descending here
    params.sortOrder = filter.direction ?? 'desc';
  }

  const url = '/v1/assembly';
  const response = await api.get(url, {
    params,
    paramsSerializer: (parameters) => queryString.stringify(parameters),
  });

  return parseResponse(assemblyListingResponseSchema, response.data, {
    schemaName: 'Assemblies',
    endpoint: url,
  });
}
